import * as React from "react";
import {Static} from "@bizform/type/decorator/Static.decorator";
import {
    BaseStateModel,
    ComponentElement,
    ComponentElementStatic
} from "@bizform/type/model/component_element/ComponentElement.model";
import {
    ComponentCategory,
    ComponentElementManifest,
    ComponentElementProperty
} from "@bizform/type/model/component_element/ComponentElementManifest.model";
import {DomAttribute} from "@bizform/type/model/dom/DomAttribute.model";
import {DataType} from "@bizform/type/model/model/DataType.model";
import {ComponentHelper, Debug, FunctionHelper} from "@bizform/helper/dist/js/bizflow/helper.min";
import {ComponentElementClass, ReactComponentElementInit} from "@bizform/core/dist/js/bizflow/core.min";
import {AbstractComponentElement} from "../AbstractComponentElement";


type Props =  {
};

type State = BaseStateModel & {
    show: boolean;
    redraw: boolean;
    customId: string,
    customHtml: string;
};

/**
 * grid template
 *
 * @since 0.0.1
 * @author Heungsoo Ham
 * @class
 */
@ComponentElementClass()
@Static<ComponentElementStatic<any>>()
export class BfTemplatePanel extends AbstractComponentElement<Props, State> {
    /* manifest */
    static readonly componentElementManifest: ComponentElementManifest = {
        uniqueId: "_vCSsD2OIzXcqng5xT2g_iNPoKkevUKk",
        category: ComponentCategory.LABEL,
        label: "Template Panel",
        description: "Template Panel",
        icon: "",
        dataType: [DataType.ANY, DataType.BOOLEAN, DataType.DATE, DataType.DATEARRAY, DataType.DATETIME, DataType.UTCDATE, DataType.UTCDATEARRAY, DataType.UTCDATETIME, DataType.INTEGER, DataType.INTEGERARRAY, DataType.LOOKUP, DataType.LOOKUPARRAY, DataType.NUMBER, DataType.NUMBERARRAY, DataType.STRING, DataType.STRINGARRAY, DataType.TIME, DataType.UTCTIME, DataType.TRISTATE, DataType.USER],
        property: null
    };

    /** action */
    subscription(value: any) {
        let v;
        switch (this.dataType) {
            case DataType.LOOKUP:
                value = value ? value : {};
                v = value ? value.label || value.value : "";
                break;
            case DataType.LOOKUPARRAY:
                value = value ? value : [];
                v = "";
                for (const k in value) {
                    if (value[k] && value[k].value) {
                        v += value[k].label || value[k].value + ", ";
                    }
                }
                v = v.replace(/, $/, "");
                break;
            case DataType.USER:
                value = value ? value : {};
                v = value && value.name ? value.name : "";
                break;
            default:
                v = value;
        }
        if (this.dataType && this.dataType.startsWith("[") && !v && Array.isArray(value)) {
            v = "";
            for (const k in value) {
                if (value[k] !== undefined) {
                    v += value[k] + ", ";
                }
            }
            v = v.replace(/, $/, "");
        } else if (this.dataType && this.dataType.startsWith("[") && !value) {
            value = [];
            v = "";
        }
        if (typeof value === "object") {
            value = JSON.stringify(value, null, 2);
        }
        this._orgValue = value;
        this._value = v;
        this._templateExecute(true).then();
    }

    onDisabled() {
        this._show();
    }
    onReadOnly() {
        this._show();
    }
    onPrint() {
        this._show();
    }
    onEdit() {
        this._show();
    }

    focus() {}

    readyComponent() {}

    setProperty(property: ComponentElementProperty) {
        try {
            this._split = false;
            const newState = ComponentHelper.createStateWithProperties<State, null>(property, this);
            this._templateExecute(true).then();
            this.setState(newState, this._templateExecute);
        } catch (e) {
            Debug.console.error("setProperty", e);
        }
    }

    noDisplayInheritance() {
    }

    @ReactComponentElementInit(BfTemplatePanel)
    static init(): ComponentElement<string> {
        return;
    }

    initDone(): void {
    }

    destroy(): void {
        this.destroyDone();
    }

    destroyDone(): void {
    }

    emitHasContainerKey(): void {
    }

    /* for React */
    props: Props;
    state: State;

    private _value: any;
    private _orgValue: any;
    private _tHtml: string;
    private _tScript: string;
    private _rHtml: string;
    private _init: boolean;
    private _split: boolean;

    constructor(props?: Props) {
        super(props);
        this.state = {
            show: false,
            redraw: false,
            customId: "",
            customHtml:  ""
        };
        this._value = "";
        this._tHtml = "";
        this._tScript = "";
    }

    shouldComponentUpdate(nextProps: Readonly<Props>, nextState: Readonly<State>, nextContext: any): boolean {
        return this.state.show || this.state.redraw !== nextState.redraw;
    }

    componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
        Debug.console.error(error, errorInfo);
    }

    render(): React.ReactNode {
        if (!this.state.show) {
            return null;
        } else {
            const attr = {
                [DomAttribute.CUSTOMID]: this.state.customId
            };
            return (
                <div dangerouslySetInnerHTML={{__html: this._rHtml}}{...attr}></div>
            );
        }
    }

    private async _templateExecute(subscription?: boolean): Promise<void> {
        if (!this._value) {
            switch (this.dataType) {
                case DataType.LOOKUP:
                case DataType.USER:
                    this._orgValue = "{}";
                    this._value = "";
                    break;
                case DataType.LOOKUPARRAY:
                    this._orgValue = "[]";
                    this._value = "";
                    break;
            }
        }
        if (this.dataType && this.dataType.startsWith("[") && !this._value) {
            this._orgValue = "[]";
            this._value = "";
        }
        if (!this._split && !subscription) {
            this._split = true;
            const t = FunctionHelper.gridTemplate(this.state.customHtml);
            this._tHtml = t && t[0] ? t[0] : "";
            this._tScript = t && t[1] ? t[1] : "";
        }
        if (this._tScript) {
            const script = this._tScript.replace(/\{\s*value\s*}/g, this._value !== undefined ? this._value : "").replace(/\{\s*orgValue\s*}/g, this._orgValue);
            const rValue = script ? FunctionHelper.execute(script) : this._value;
            this._rHtml = this._tHtml.replace(/\{\s*value\s*}/g, rValue !== undefined ? rValue : "");
        } else {
            this._rHtml = this._tHtml.replace(/\{\s*value\s*}/g, this._value !== undefined ? this._value : "");
        }
        this._rHtml = this._rHtml !== undefined ? this._rHtml : "";
        this._show();
        if (!this._init) {
            this._init = true;
            await FunctionHelper.timeout(100);
            this.readyComponent();
        }
    }

    private _show() {
        this.setState({
            show: true,
            redraw: this.state.redraw ? false : true
        });
    }
}
